/**
 * ToolsEditor - UI for defining custom client tools
 *
 * Allows users to create tools that the voice agent can call to control the UI.
 * Each tool has a name, description, action type, optional parameters,
 * and a response template that tells the agent what happened.
 *
 * When screens data is provided, action fields show dropdown pickers
 * for screen IDs and widget IDs instead of raw text inputs.
 *
 * Tools are saved as part of AppSettings.agent.clientTools.
 */

import { useState, useCallback, useMemo } from 'react';
import type { ClientToolDefinition, ClientToolAction } from '@deep-agents/shared-types';

// ─────────────────────────────────────────────────────────────
// Types for canvas context (screens and widgets)
// ─────────────────────────────────────────────────────────────

/** A screen on the canvas with its widgets */
interface ScreenInfo {
  id: string;
  title: string;
  isHome: boolean;
  widgets?: Array<{ id: string; type: string; label: string }>;
}

/** A flattened widget entry with its parent screen info */
interface WidgetInfo {
  id: string;
  type: string;
  label: string;
  screenTitle: string;
}

// ─────────────────────────────────────────────────────────────
// Action type options for the dropdown
// ─────────────────────────────────────────────────────────────

/** Human-readable labels and descriptions for each action type */
const ACTION_TYPE_OPTIONS: Array<{
  value: ClientToolAction['type'];
  label: string;
  description: string;
}> = [
  { value: 'navigate', label: 'Navigate', description: 'Go to a different screen' },
  { value: 'setState', label: 'Set State', description: 'Update a state value' },
  { value: 'increment', label: 'Increment', description: 'Increase a numeric value' },
  { value: 'decrement', label: 'Decrement', description: 'Decrease a numeric value' },
  { value: 'reset', label: 'Reset', description: 'Reset a value to its initial state' },
  { value: 'toast', label: 'Show Toast', description: 'Display a notification message' },
  { value: 'showModal', label: 'Show Modal', description: 'Show a modal overlay' },
  { value: 'hideModal', label: 'Hide Modal', description: 'Hide a modal overlay' },
  { value: 'toggleView', label: 'Toggle View', description: 'Show/hide a view section' },
  { value: 'showWidget', label: 'Show Widget', description: 'Make a widget visible' },
  { value: 'hideWidget', label: 'Hide Widget', description: 'Hide a widget' },
  { value: 'appendToList', label: 'Add to List', description: 'Add items to a list' },
  { value: 'filterList', label: 'Filter List', description: 'Filter a list by field' },
  { value: 'clearList', label: 'Clear List', description: 'Remove all items from a list' },
];

// ─────────────────────────────────────────────────────────────
// Helper: create a default action for a given type
// ─────────────────────────────────────────────────────────────

function createDefaultAction(type: ClientToolAction['type']): ClientToolAction {
  switch (type) {
    case 'navigate': return { type: 'navigate', screenId: '' };
    case 'setState': return { type: 'setState', path: '', value: '' };
    case 'increment': return { type: 'increment', path: '', amount: 1 };
    case 'decrement': return { type: 'decrement', path: '', amount: 1 };
    case 'reset': return { type: 'reset', path: '' };
    case 'toast': return { type: 'toast', message: '' };
    case 'showModal': return { type: 'showModal', modalId: '' };
    case 'hideModal': return { type: 'hideModal', modalId: '' };
    case 'toggleView': return { type: 'toggleView', viewId: '' };
    case 'showWidget': return { type: 'showWidget', widgetId: '' };
    case 'hideWidget': return { type: 'hideWidget', widgetId: '' };
    case 'appendToList': return { type: 'appendToList', listId: '', items: [] };
    case 'filterList': return { type: 'filterList', listId: '', field: '', value: '' };
    case 'clearList': return { type: 'clearList', listId: '' };
    default: return { type: 'navigate', screenId: '' };
  }
}

// ─────────────────────────────────────────────────────────────
// Helper: create a blank tool definition
// ─────────────────────────────────────────────────────────────

function createBlankTool(): ClientToolDefinition {
  return {
    name: '',
    description: '',
    responseTemplate: '',
    action: { type: 'navigate', screenId: '' },
    parameters: [],
  };
}

// ─────────────────────────────────────────────────────────────
// Props
// ─────────────────────────────────────────────────────────────

interface ToolsEditorProps {
  /** Current list of custom tool definitions */
  tools: ClientToolDefinition[];
  /** Called when the tools list changes */
  onChange: (tools: ClientToolDefinition[]) => void;
  /** Connected screens with their widgets (for smart dropdowns) */
  screens?: ScreenInfo[];
}

// ─────────────────────────────────────────────────────────────
// Main Component
// ─────────────────────────────────────────────────────────────

export function ToolsEditor({ tools, onChange, screens }: ToolsEditorProps) {
  // Track which tool is being edited (index), or null if none
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  // Draft state for the tool being edited
  const [draft, setDraft] = useState<ClientToolDefinition>(createBlankTool());

  // Build a flat list of all widgets across all screens for dropdown pickers
  const allWidgets = useMemo((): WidgetInfo[] => {
    if (!screens) return [];
    const widgets: WidgetInfo[] = [];
    for (const screen of screens) {
      for (const w of screen.widgets || []) {
        widgets.push({
          id: w.id,
          type: w.type,
          label: w.label,
          screenTitle: screen.title,
        });
      }
    }
    return widgets;
  }, [screens]);

  // Filter to only list widgets (for list-specific actions)
  const listWidgets = useMemo(
    () => allWidgets.filter((w) => w.type === 'list'),
    [allWidgets]
  );

  // --- Add a new tool ---
  const handleAdd = useCallback(() => {
    const newTool = createBlankTool();
    newTool.name = `tool_${tools.length + 1}`;
    newTool.description = 'Describe what this tool does';
    newTool.responseTemplate = 'Action completed successfully.';
    setDraft(newTool);
    setEditingIndex(tools.length); // Will be a new entry
  }, [tools.length]);

  // --- Start editing an existing tool ---
  const handleEdit = useCallback((index: number) => {
    setDraft({ ...tools[index], parameters: [...(tools[index].parameters || [])] });
    setEditingIndex(index);
  }, [tools]);

  // --- Save the draft back to the tools list ---
  const handleSave = useCallback(() => {
    if (editingIndex === null) return;
    // Validate required fields
    if (!draft.name.trim() || !draft.description.trim()) return;

    const updated = [...tools];
    if (editingIndex >= tools.length) {
      // Adding a new tool
      updated.push(draft);
    } else {
      // Updating existing tool
      updated[editingIndex] = draft;
    }
    onChange(updated);
    setEditingIndex(null);
  }, [editingIndex, draft, tools, onChange]);

  // --- Cancel editing ---
  const handleCancel = useCallback(() => {
    setEditingIndex(null);
  }, []);

  // --- Delete a tool ---
  const handleDelete = useCallback((index: number) => {
    const updated = tools.filter((_, i) => i !== index);
    onChange(updated);
    // If we were editing the deleted tool, close the editor
    if (editingIndex === index) setEditingIndex(null);
  }, [tools, onChange, editingIndex]);

  // --- Add a parameter to the draft ---
  const handleAddParam = useCallback(() => {
    setDraft((prev) => ({
      ...prev,
      parameters: [
        ...(prev.parameters || []),
        { name: '', type: 'string' as const, description: '', required: false },
      ],
    }));
  }, []);

  // --- Remove a parameter from the draft ---
  const handleRemoveParam = useCallback((paramIndex: number) => {
    setDraft((prev) => ({
      ...prev,
      parameters: (prev.parameters || []).filter((_, i) => i !== paramIndex),
    }));
  }, []);

  // --- Update a parameter field ---
  const handleUpdateParam = useCallback(
    (paramIndex: number, field: string, value: unknown) => {
      setDraft((prev) => ({
        ...prev,
        parameters: (prev.parameters || []).map((p, i) =>
          i === paramIndex ? { ...p, [field]: value } : p
        ),
      }));
    },
    []
  );

  // --- Change the action type (resets action config) ---
  const handleActionTypeChange = useCallback((newType: string) => {
    setDraft((prev) => ({
      ...prev,
      action: createDefaultAction(newType as ClientToolAction['type']),
    }));
  }, []);

  // --- Update a field on the action ---
  const handleActionFieldChange = useCallback((field: string, value: unknown) => {
    setDraft((prev) => ({
      ...prev,
      action: { ...prev.action, [field]: value } as ClientToolAction,
    }));
  }, []);

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wide">
            Custom Tools
          </h3>
          <p className="text-xs text-gray-400 mt-1">
            Define tools the voice agent can call to control your app's UI
          </p>
        </div>
        {editingIndex === null && (
          <button
            onClick={handleAdd}
            className="flex items-center gap-1 px-3 py-1.5 text-sm font-medium text-violet-600 bg-violet-50 rounded-lg hover:bg-violet-100 transition-colors"
          >
            <span className="text-lg leading-none">+</span> Add Tool
          </button>
        )}
      </div>

      {/* Tool List (shown when not editing) */}
      {editingIndex === null && (
        <div className="space-y-2">
          {tools.length === 0 ? (
            // Empty state
            <div className="border-2 border-dashed border-gray-200 rounded-lg p-6 text-center">
              <p className="text-sm text-gray-500 mb-2">No custom tools defined yet</p>
              <p className="text-xs text-gray-400">
                Add tools to let the voice agent show modals, filter lists, update widgets, and more.
              </p>
            </div>
          ) : (
            tools.map((tool, index) => (
              <div
                key={`${tool.name}-${index}`}
                className="group flex items-center justify-between px-4 py-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium text-gray-900">{tool.name}</span>
                    <span className="text-xs px-1.5 py-0.5 bg-violet-100 text-violet-700 rounded">
                      {tool.action.type}
                    </span>
                  </div>
                  <p className="text-xs text-gray-500 mt-0.5 truncate">{tool.description}</p>
                </div>
                <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                  <button
                    onClick={() => handleEdit(index)}
                    className="p-1.5 text-gray-400 hover:text-violet-600 rounded"
                    title="Edit tool"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                    </svg>
                  </button>
                  <button
                    onClick={() => handleDelete(index)}
                    className="p-1.5 text-gray-400 hover:text-red-600 rounded"
                    title="Delete tool"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                  </button>
                </div>
              </div>
            ))
          )}
        </div>
      )}

      {/* Tool Edit Form (shown when editing) */}
      {editingIndex !== null && (
        <div className="border border-violet-200 rounded-lg p-4 space-y-4 bg-violet-50/30">
          <h4 className="text-sm font-medium text-gray-900">
            {editingIndex >= tools.length ? 'New Tool' : 'Edit Tool'}
          </h4>

          {/* Tool Name */}
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">
              Tool Name
            </label>
            <input
              type="text"
              value={draft.name}
              onChange={(e) => setDraft({ ...draft, name: e.target.value.replace(/\s/g, '_') })}
              placeholder="e.g., showProducts"
              className="w-full px-3 py-2 text-sm text-gray-900 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-transparent"
            />
            <p className="text-xs text-gray-400 mt-1">
              No spaces allowed. The agent will call this by name.
            </p>
          </div>

          {/* Description */}
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">
              Description
            </label>
            <input
              type="text"
              value={draft.description}
              onChange={(e) => setDraft({ ...draft, description: e.target.value })}
              placeholder="e.g., Display the product selection modal"
              className="w-full px-3 py-2 text-sm text-gray-900 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-transparent"
            />
            <p className="text-xs text-gray-400 mt-1">
              Tell the agent when to use this tool
            </p>
          </div>

          {/* Action Type */}
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">
              Action Type
            </label>
            <select
              value={draft.action.type}
              onChange={(e) => handleActionTypeChange(e.target.value)}
              className="w-full px-3 py-2 text-sm text-gray-900 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500"
            >
              {ACTION_TYPE_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label} - {opt.description}
                </option>
              ))}
            </select>
          </div>

          {/* Action Configuration (varies by type) */}
          <ActionConfigFields
            action={draft.action}
            onFieldChange={handleActionFieldChange}
            screens={screens}
            allWidgets={allWidgets}
            listWidgets={listWidgets}
          />

          {/* Parameters */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="block text-xs font-medium text-gray-600">
                Parameters (optional)
              </label>
              <button
                onClick={handleAddParam}
                className="text-xs text-violet-600 hover:text-violet-800"
              >
                + Add Parameter
              </button>
            </div>
            {(draft.parameters || []).length === 0 ? (
              <p className="text-xs text-gray-400">
                No parameters. The agent calls this tool without any inputs.
              </p>
            ) : (
              <div className="space-y-2">
                {(draft.parameters || []).map((param, i) => (
                  <div key={i} className="flex items-start gap-2 p-2 bg-white rounded border border-gray-200">
                    <div className="flex-1 space-y-1">
                      <input
                        type="text"
                        value={param.name}
                        onChange={(e) => handleUpdateParam(i, 'name', e.target.value.replace(/\s/g, ''))}
                        placeholder="paramName"
                        className="w-full px-2 py-1 text-xs border border-gray-200 rounded focus:outline-none focus:ring-1 focus:ring-violet-500"
                      />
                      <input
                        type="text"
                        value={param.description}
                        onChange={(e) => handleUpdateParam(i, 'description', e.target.value)}
                        placeholder="Description of this parameter"
                        className="w-full px-2 py-1 text-xs border border-gray-200 rounded focus:outline-none focus:ring-1 focus:ring-violet-500"
                      />
                      <div className="flex items-center gap-2">
                        <select
                          value={param.type}
                          onChange={(e) => handleUpdateParam(i, 'type', e.target.value)}
                          className="px-2 py-1 text-xs border border-gray-200 rounded"
                        >
                          <option value="string">string</option>
                          <option value="number">number</option>
                          <option value="boolean">boolean</option>
                        </select>
                        <label className="flex items-center gap-1 text-xs text-gray-500">
                          <input
                            type="checkbox"
                            checked={param.required || false}
                            onChange={(e) => handleUpdateParam(i, 'required', e.target.checked)}
                            className="rounded border-gray-300"
                          />
                          Required
                        </label>
                      </div>
                    </div>
                    <button
                      onClick={() => handleRemoveParam(i)}
                      className="p-1 text-gray-400 hover:text-red-500"
                      title="Remove parameter"
                    >
                      <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Response Template */}
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">
              Response Template
            </label>
            <textarea
              value={draft.responseTemplate}
              onChange={(e) => setDraft({ ...draft, responseTemplate: e.target.value })}
              placeholder="e.g., Product selector is now visible. User will choose a product."
              rows={2}
              className="w-full px-3 py-2 text-sm text-gray-900 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-transparent resize-none"
            />
            <p className="text-xs text-gray-400 mt-1">
              Placeholders: {'{{result}}'}, {'{{params.name}}'}, {'{{state.path}}'}
            </p>
          </div>

          {/* Save / Cancel */}
          <div className="flex justify-end gap-2 pt-2 border-t border-gray-200">
            <button
              onClick={handleCancel}
              className="px-3 py-1.5 text-sm text-gray-600 hover:text-gray-800 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={!draft.name.trim() || !draft.description.trim()}
              className="px-3 py-1.5 text-sm font-medium text-white bg-violet-600 rounded-lg hover:bg-violet-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {editingIndex >= tools.length ? 'Add Tool' : 'Save Changes'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// SmartSelect - Dropdown with canvas items + custom text fallback
// ─────────────────────────────────────────────────────────────

/**
 * A dropdown that shows items from the canvas (screens, widgets, lists)
 * plus a "Custom..." option for manually typing a value.
 * If no canvas items are available, falls back to a regular text input.
 */
function SmartSelect({
  label,
  value,
  onChange,
  options,
  placeholder,
  hint,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  options: Array<{ value: string; label: string; detail?: string }>;
  placeholder: string;
  hint?: string;
}) {
  // If the current value isn't in the dropdown options, show the custom input
  const isCustomValue = value && !options.some((o) => o.value === value);
  const [showCustom, setShowCustom] = useState(isCustomValue);

  // No options available — fall back to plain text input
  if (options.length === 0) {
    return (
      <div>
        <label className="block text-xs font-medium text-gray-600 mb-1">{label}</label>
        <input
          type="text"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          className="w-full px-3 py-2 text-sm text-gray-900 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-transparent"
        />
        {hint && <p className="text-xs text-gray-400 mt-1">{hint}</p>}
      </div>
    );
  }

  return (
    <div>
      <label className="block text-xs font-medium text-gray-600 mb-1">{label}</label>
      {showCustom ? (
        // Custom text input mode
        <div className="flex gap-2">
          <input
            type="text"
            value={value}
            onChange={(e) => onChange(e.target.value)}
            placeholder={placeholder}
            className="flex-1 px-3 py-2 text-sm text-gray-900 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-transparent"
          />
          <button
            type="button"
            onClick={() => setShowCustom(false)}
            className="px-2 py-1 text-xs text-violet-600 border border-violet-200 rounded-lg hover:bg-violet-50"
            title="Switch to dropdown picker"
          >
            Pick
          </button>
        </div>
      ) : (
        // Dropdown picker mode
        <div className="flex gap-2">
          <select
            value={value}
            onChange={(e) => {
              if (e.target.value === '__custom__') {
                setShowCustom(true);
                onChange('');
              } else {
                onChange(e.target.value);
              }
            }}
            className="flex-1 px-3 py-2 text-sm text-gray-900 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500"
          >
            <option value="">-- Select --</option>
            {options.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}{opt.detail ? ` (${opt.detail})` : ''}
              </option>
            ))}
            <option value="__custom__">Custom...</option>
          </select>
        </div>
      )}
      {hint && <p className="text-xs text-gray-400 mt-1">{hint}</p>}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// ActionConfigFields - Renders config inputs based on action type
// ─────────────────────────────────────────────────────────────

function ActionConfigFields({
  action,
  onFieldChange,
  screens,
  allWidgets,
  listWidgets,
}: {
  action: ClientToolAction;
  onFieldChange: (field: string, value: unknown) => void;
  screens?: ScreenInfo[];
  allWidgets: WidgetInfo[];
  listWidgets: WidgetInfo[];
}) {
  // Build option arrays for SmartSelect dropdowns
  const screenOptions = useMemo(
    () =>
      (screens || []).map((s) => ({
        value: s.id,
        label: `${s.title}${s.isHome ? ' (Home)' : ''}`,
      })),
    [screens]
  );

  const widgetOptions = useMemo(
    () =>
      allWidgets.map((w) => ({
        value: w.id,
        label: `${w.label}`,
        detail: `${w.type} — ${w.screenTitle}`,
      })),
    [allWidgets]
  );

  const listOptions = useMemo(
    () =>
      listWidgets.map((w) => ({
        value: w.id,
        label: `${w.label}`,
        detail: w.screenTitle,
      })),
    [listWidgets]
  );

  // Helper for plain text input (no dropdown needed)
  const renderInput = (label: string, field: string, value: string, placeholder: string, hint?: string) => (
    <div>
      <label className="block text-xs font-medium text-gray-600 mb-1">{label}</label>
      <input
        type="text"
        value={value}
        onChange={(e) => onFieldChange(field, e.target.value)}
        placeholder={placeholder}
        className="w-full px-3 py-2 text-sm text-gray-900 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-transparent"
      />
      {hint && <p className="text-xs text-gray-400 mt-1">{hint}</p>}
    </div>
  );

  switch (action.type) {
    case 'navigate':
      return (
        <SmartSelect
          label="Target Screen"
          value={(action as { screenId: string }).screenId}
          onChange={(v) => onFieldChange('screenId', v)}
          options={screenOptions}
          placeholder="e.g., home, settings"
          hint="The screen to navigate to"
        />
      );

    case 'setState':
      return (
        <div className="space-y-3">
          <SmartSelect
            label="Target Widget"
            value={(action as { path: string }).path}
            onChange={(v) => onFieldChange('path', v)}
            options={widgetOptions}
            placeholder="e.g., plan-list"
            hint="The widget whose state to update"
          />
          {renderInput('Default Value', 'value', String((action as { value?: unknown }).value || ''), 'e.g., true, "hello"', 'Leave blank to use agent parameter')}
        </div>
      );

    case 'increment':
    case 'decrement':
      return (
        <div className="space-y-3">
          <SmartSelect
            label="Target Widget"
            value={(action as { path: string }).path}
            onChange={(v) => onFieldChange('path', v)}
            options={widgetOptions.filter((w) => w.detail?.includes('counter') || w.detail?.includes('slider'))}
            placeholder="e.g., counter"
            hint="The numeric widget to change"
          />
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Amount</label>
            <input
              type="number"
              value={(action as { amount?: number }).amount ?? 1}
              onChange={(e) => onFieldChange('amount', Number(e.target.value))}
              className="w-full px-3 py-2 text-sm text-gray-900 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-transparent"
            />
          </div>
        </div>
      );

    case 'reset':
      return (
        <SmartSelect
          label="Target Widget"
          value={(action as { path: string }).path}
          onChange={(v) => onFieldChange('path', v)}
          options={widgetOptions}
          placeholder="e.g., counter"
          hint="The widget to reset"
        />
      );

    case 'toast':
      return (
        <div className="space-y-3">
          {renderInput('Message', 'message', (action as { message: string }).message, 'e.g., Item added!', 'The notification text to show')}
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Variant</label>
            <select
              value={(action as { variant?: string }).variant || 'info'}
              onChange={(e) => onFieldChange('variant', e.target.value)}
              className="w-full px-3 py-2 text-sm text-gray-900 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500"
            >
              <option value="info">Info</option>
              <option value="success">Success</option>
              <option value="warning">Warning</option>
              <option value="error">Error</option>
            </select>
          </div>
        </div>
      );

    case 'showModal':
    case 'hideModal':
      return renderInput('Modal ID', 'modalId', (action as { modalId: string }).modalId, 'e.g., product-selector', 'Identifier for the modal to show/hide');

    case 'toggleView':
      return renderInput('View ID', 'viewId', (action as { viewId: string }).viewId, 'e.g., filters-panel', 'Identifier for the view section to toggle');

    case 'showWidget':
    case 'hideWidget':
      return (
        <SmartSelect
          label="Target Widget"
          value={(action as { widgetId: string }).widgetId}
          onChange={(v) => onFieldChange('widgetId', v)}
          options={widgetOptions}
          placeholder="e.g., widget-abc123"
          hint="The widget to show/hide"
        />
      );

    case 'appendToList':
      return (
        <SmartSelect
          label="Target List"
          value={(action as { listId: string }).listId}
          onChange={(v) => onFieldChange('listId', v)}
          options={listOptions}
          placeholder="e.g., exercises"
          hint="The list widget to add items to"
        />
      );

    case 'filterList':
      return (
        <div className="space-y-3">
          <SmartSelect
            label="Target List"
            value={(action as { listId: string }).listId}
            onChange={(v) => onFieldChange('listId', v)}
            options={listOptions}
            placeholder="e.g., exercises"
            hint="The list widget to filter"
          />
          {renderInput('Filter Field', 'field', (action as { field: string }).field, 'e.g., category', 'Which field to filter by')}
          {renderInput('Filter Value', 'value', String((action as { value: unknown }).value || ''), 'e.g., back', 'Leave blank to use agent parameter')}
        </div>
      );

    case 'clearList':
      return (
        <SmartSelect
          label="Target List"
          value={(action as { listId: string }).listId}
          onChange={(v) => onFieldChange('listId', v)}
          options={listOptions}
          placeholder="e.g., exercises"
          hint="The list widget to clear"
        />
      );

    default:
      return null;
  }
}
